import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { add, format } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useMoveActionsMutation } from "@/store/moves/apis";
import { Move, User } from "@/types";
import { DialogDescription } from "@/components/ui/dialog";
import { useSelector } from "react-redux";
import { getSelectedMover } from "@/store/mover/slice";
import { formatAddress } from "@/lib/utils";

interface ScheduleOnsiteProps {
  move: Move;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const ScheduleOnsite: React.FC<ScheduleOnsiteProps> = ({
  move,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(
    move.upcoming_onsite_event?.date
      ? new Date(move.upcoming_onsite_event.date)
      : undefined
  );
  const [startTimeInput, setStartTimeInput] = useState(
    move.upcoming_onsite_event?.start_time || ""
  );
  const [endTimeInput, setEndTimeInput] = useState(
    move.upcoming_onsite_event?.end_time || ""
  );
  const [selectedUser, setSelectedUser] = useState<string>(
    move.upcoming_onsite_event?.assigned_to_id?.toString() || ""
  );
  const [title, setTitle] = useState(
    move.upcoming_onsite_event?.title ||
      `${move.customer?.name || "Customer"} Onsite`
  );
  const [notes, setNotes] = useState(move.upcoming_onsite_event?.notes || "");
  const [address, setAddress] = useState(
    move.upcoming_onsite_event?.address ||
      (move.origin ? formatAddress(move.origin) : "")
  );
  const [moveActions] = useMoveActionsMutation();
  const { toast } = useToast();
  const selectedMover = useSelector(getSelectedMover);

  const formatTime = (
    hour: number | null,
    minute: number | null,
    meridiem: string | null
  ) => {
    if (hour === null || minute === null || meridiem === null) {
      return "";
    }
    let displayHour = hour;
    if (hour > 12) {
      displayHour = hour - 12;
    } else if (hour === 0) {
      displayHour = 12;
    }
    return `${displayHour}:${minute.toString().padStart(2, "0")}${meridiem}`;
  };

  const parseTime = (timeString: string) => {
    const match = timeString.match(/^(\d{1,2})(?::?(\d{2}))?\s*(am|pm)?$/i);
    if (match) {
      const [, hour, minute, meridiem] = match;
      let parsedHour = parseInt(hour, 10);
      const parsedMinute = minute ? parseInt(minute, 10) : 0;
      const parsedMeridiem = meridiem
        ? meridiem.toUpperCase()
        : parsedHour < 12
        ? "AM"
        : "PM";
      if (parsedHour > 12) {
        parsedHour = parsedHour - 12;
      } else if (parsedHour === 0) {
        parsedHour = 12;
      }
      return {
        hour: parsedHour,
        minute: parsedMinute,
        meridiem: parsedMeridiem,
      };
    }
    return {
      hour: null,
      minute: null,
      meridiem: null,
    };
  };

  const handleTimeBlur = (isStartTime: boolean) => {
    const timeInput = isStartTime ? startTimeInput : endTimeInput;
    const parsed = parseTime(timeInput);
    if (
      parsed &&
      parsed.hour !== null &&
      parsed.minute !== null &&
      parsed.meridiem !== null
    ) {
      const formattedTime = formatTime(
        parsed.hour,
        parsed.minute,
        parsed.meridiem
      );
      if (isStartTime) {
        setStartTimeInput(formattedTime);
      } else {
        setEndTimeInput(formattedTime);
      }
    }
  };

  const handleSubmit = async () => {
    if (
      !date ||
      !startTimeInput ||
      !endTimeInput ||
      !selectedUser ||
      !title ||
      !address
    ) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    const startTimeParsed = parseTime(startTimeInput);
    const endTimeParsed = parseTime(endTimeInput);

    if (
      !startTimeParsed ||
      startTimeParsed.hour === null ||
      startTimeParsed.minute === null ||
      !startTimeParsed.meridiem ||
      !endTimeParsed ||
      endTimeParsed.hour === null ||
      endTimeParsed.minute === null ||
      !endTimeParsed.meridiem
    ) {
      toast({
        title: "Error",
        description: "Invalid time format",
        variant: "destructive",
      });
      return;
    }

    const data = {
      date: format(date, "yyyy-MM-dd"),
      start_time: `${startTimeParsed.hour}:${startTimeParsed.minute
        .toString()
        .padStart(2, "0")}${startTimeParsed.meridiem}`,
      end_time: `${endTimeParsed.hour}:${endTimeParsed.minute
        .toString()
        .padStart(2, "0")}${endTimeParsed.meridiem}`,
      assigned_to_id: selectedUser,
      title,
      notes,
      address,
    };

    try {
      await moveActions({
        id: move.id,
        action: "schedule_onsite",
        data,
      }).unwrap();

      toast({
        title: "Success",
        description: "Onsite scheduled successfully",
      });
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("API Error:", error);
      toast({
        title: "Error",
        description: "Failed to schedule onsite",
        variant: "destructive",
      });
    }
  };

  const availableUsers =
    selectedMover?.users.filter((user: User) => user.owner || user.sales) || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="max-h-[100vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {move.upcoming_onsite_event
              ? "Re-schedule Onsite"
              : "Schedule Onsite"}
          </SheetTitle>
          <DialogDescription>
            Schedule an onsite visit for this move.
          </DialogDescription>
        </SheetHeader>
        <div className="flex flex-col gap-4 mt-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Title *</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter event title"
              className="w-full"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Address *</label>
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter onsite address"
              className="w-full"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Date *</label>
            <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? (
                    format(add(date, { hours: 12 }), "MM/dd/yyyy")
                  ) : (
                    <span>Date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => {
                    setDate(d);
                    setDatePopoverOpen(false);
                  }}
                  disabled={(d) => {
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    return d < yesterday;
                  }}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex flex-row gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Start Time *</label>
              <Input
                value={startTimeInput}
                onChange={(e) => setStartTimeInput(e.target.value)}
                onBlur={() => handleTimeBlur(true)}
                placeholder="e.g. 9am, 2:30pm"
                className="w-full"
                onFocus={(e) => e.target.select()}
              />
              {/* <span className="text-xs text-muted-foreground">
                (eg 8am, 130pm)
              </span> */}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">End Time *</label>
              <Input
                value={endTimeInput}
                onChange={(e) => setEndTimeInput(e.target.value)}
                onBlur={() => handleTimeBlur(false)}
                placeholder="e.g. 10am, 3:30pm"
                className="w-full"
                onFocus={(e) => e.target.select()}
              />
              {/* <span className="text-xs text-muted-foreground">
                (eg 9am, 230pm)
              </span> */}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Assigned To *</label>
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger>
                <SelectValue placeholder="Select a user" />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.map((user: User) => (
                  <SelectItem key={user.id} value={user.id?.toString() || ""}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Notes</label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes"
            />
          </div>

          <SheetFooter className="w-full sticky -bottom-4 bg-white">
            <Button onClick={handleSubmit} className="w-full">
              {move.upcoming_onsite_event
                ? "Re-schedule Onsite"
                : "Schedule Onsite"}
            </Button>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ScheduleOnsite;
